"use client";

import React, { createContext, useContext, useEffect } from "react";

type LayoutOverrides = {
    showHeader?: boolean;
    showBreadcrumbs?: boolean;
    standalone?: boolean;
};

const LayoutOverridesContext = createContext<LayoutOverrides>({
    showHeader: true,
    showBreadcrumbs: true,
    standalone: false,
});

const HEADER_HIDDEN_CLASS = "layout-hide-header";
const BREADCRUMBS_HIDDEN_CLASS = "layout-hide-breadcrumbs";
const STANDALONE_CLASS = "layout-standalone";

export const LayoutOverridesProvider: React.FC<
    React.PropsWithChildren<LayoutOverrides>
> = ({
    children,
    showHeader = true,
    showBreadcrumbs = true,
    standalone = false,
}) => {
    useEffect(() => {
        const body = document.body;
        if (!body) return;

        if (showHeader === false) {
            body.classList.add(HEADER_HIDDEN_CLASS);
        } else {
            body.classList.remove(HEADER_HIDDEN_CLASS);
        }

        if (showBreadcrumbs === false) {
            body.classList.add(BREADCRUMBS_HIDDEN_CLASS);
        } else {
            body.classList.remove(BREADCRUMBS_HIDDEN_CLASS);
        }

        if (standalone) {
            body.classList.add(STANDALONE_CLASS);
        } else {
            body.classList.remove(STANDALONE_CLASS);
        }

        return () => {
            body.classList.remove(HEADER_HIDDEN_CLASS);
            body.classList.remove(BREADCRUMBS_HIDDEN_CLASS);
            body.classList.remove(STANDALONE_CLASS);
        };
    }, [showHeader, showBreadcrumbs, standalone]);

    return (
        <LayoutOverridesContext.Provider
            value={{ showHeader, showBreadcrumbs, standalone }}
        >
            {children}
        </LayoutOverridesContext.Provider>
    );
};

export const useLayoutOverrides = () => {
    return useContext(LayoutOverridesContext);
};
